import { useState, useContext, useRef } from "react";
import { AuthContext, API } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { ArrowLeft, Database, Download, Upload, AlertTriangle } from "lucide-react";

export default function BackupPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isCreatingMediaBackup, setIsCreatingMediaBackup] = useState(false);
  const fileInputRef = useRef(null);

  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    try {
      const response = await axios.post(`${API}/backup/create`, {}, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = "backup.zip";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Backup erfolgreich erstellt und heruntergeladen");
    } catch (error) {
      console.error("Backup error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen des Backups");
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        toast.error("Bitte wählen Sie eine ZIP-Datei aus");
        return;
      }
      setSelectedFile(file);
      setShowRestoreDialog(true);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;
    
    setIsRestoring(true);
    setShowRestoreDialog(false);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/backup/restore`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success(`Backup erfolgreich wiederhergestellt! ${response.data.articles_count} Artikel, ${response.data.categories_count} Kategorien`);
      
      // Reload page after 2 seconds to show updated data
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("Restore error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Wiederherstellen des Backups");
    } finally {
      setIsRestoring(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleMediaBackup = async () => {
    setIsCreatingMediaBackup(true);
    try {
      await axios.post(`${API}/backup/media-download`);
      toast.success("Media-Backup gestartet! Sie finden es auf der Downloads-Seite.");
      // Navigate to downloads page after a short delay
      setTimeout(() => navigate("/downloads"), 1500);
    } catch (error) {
      console.error("Media backup error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen des Media-Backups");
    } finally {
      setIsCreatingMediaBackup(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Zugriff verweigert</CardTitle>
            <CardDescription>
              Nur Administratoren haben Zugriff auf die Backup-Funktion.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/")} className="w-full">
              Zurück zum Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                size="icon"
                className="border-slate-300"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <Database className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Datenbank Backup</h1>
                  <p className="text-sm text-slate-600">Sichern und wiederherstellen Sie Ihre Daten</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Create Backup Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Download className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Backup erstellen</CardTitle>
                  <CardDescription className="text-sm">
                    Vollständige Sicherung herunterladen
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Inhalt des Backups:</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Alle Artikel mit Details</li>
                  <li>• Alle Kategorien</li>
                  <li>• Alle hochgeladenen Bilder</li>
                  <li>• Benutzerdaten</li>
                  <li>• Media-Metadaten (URLs, Keywords, Beschreibungen)</li>
                </ul>
                <p className="text-xs text-slate-500 mt-3">
                  Hinweis: Media-Dateien selbst können separat heruntergeladen werden
                </p>
              </div>
              
              <Button
                onClick={handleCreateBackup}
                disabled={isCreatingBackup}
                className="w-full bg-green-600 hover:bg-green-700 text-white h-11"
              >
                {isCreatingBackup ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Backup wird erstellt...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Backup herunterladen
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Restore Backup Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Upload className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Backup wiederherstellen</CardTitle>
                  <CardDescription className="text-sm">
                    Daten aus einer Sicherung importieren
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-red-900 mb-1">Warnung!</h3>
                    <p className="text-sm text-red-700">
                      Das Wiederherstellen überschreibt alle aktuellen Artikel und Kategorien. 
                      Diese Aktion kann nicht rückgängig gemacht werden.
                    </p>
                  </div>
                </div>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isRestoring}
                variant="outline"
                className="w-full border-orange-300 text-orange-700 hover:bg-orange-50 h-11"
              >
                {isRestoring ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                    Wiederherstellung läuft...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Backup-Datei auswählen
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Media Backup Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                  <Download className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Media-Dateien sichern</CardTitle>
                  <CardDescription className="text-sm">
                    Alle hochgeladenen Media-Dateien herunterladen
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Inhalt des Media-Backups:</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Alle Media-Dateien (Bilder, Videos, PDFs, Dokumente)</li>
                  <li>• Organisiert nach Dateityp</li>
                  <li>• Inklusive Metadaten (JSON)</li>
                </ul>
                <p className="text-xs text-slate-500 mt-3">
                  Hinweis: Wird als asynchroner Job erstellt und auf der Downloads-Seite bereitgestellt
                </p>
              </div>
              
              <Button
                onClick={handleMediaBackup}
                disabled={isCreatingMediaBackup}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white h-11"
              >
                {isCreatingMediaBackup ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Media-Backup wird gestartet...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Media-Dateien sichern
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Information Section */}
        <Card className="mt-6 border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Hinweise zur Backup-Funktion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-slate-600">
              <div>
                <h4 className="font-medium text-slate-900 mb-1">📦 Backup erstellen</h4>
                <p>
                  Erstellt eine ZIP-Datei mit allen Daten und Bildern. Speichern Sie diese Datei 
                  an einem sicheren Ort. Die Datei kann später verwendet werden, um die Datenbank 
                  wiederherzustellen.
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-slate-900 mb-1">🔄 Backup wiederherstellen</h4>
                <p>
                  Stellt die Datenbank aus einer vorher erstellten Backup-Datei wieder her. 
                  Alle aktuellen Artikel und Kategorien werden durch die Daten aus dem Backup ersetzt. 
                  Benutzerdaten werden nur wiederhergestellt, wenn sie nicht bereits existieren.
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-slate-900 mb-1">⚠️ Wichtige Hinweise</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Erstellen Sie regelmäßig Backups, um Datenverlust zu vermeiden</li>
                  <li>Bewahren Sie Backup-Dateien an einem sicheren Ort auf</li>
                  <li>Testen Sie Backups gelegentlich, um sicherzustellen, dass sie funktionieren</li>
                  <li>Die Wiederherstellung kann einige Minuten dauern bei großen Datenmengen</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Backup wiederherstellen?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p className="font-medium text-slate-900">
                Möchten Sie das Backup wirklich wiederherstellen?
              </p>
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-sm text-red-700">
                  <strong>Achtung:</strong> Alle aktuellen Artikel, Kategorien und Bilder werden 
                  gelöscht und durch die Daten aus dem Backup ersetzt. Diese Aktion kann nicht 
                  rückgängig gemacht werden.
                </p>
              </div>
              {selectedFile && (
                <p className="text-sm text-slate-600">
                  <strong>Ausgewählte Datei:</strong> {selectedFile.name}
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setSelectedFile(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Ja, wiederherstellen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
